import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Landmark, LogOut, ShieldCheck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function Layout({ children }) {
    const { t, lang, toggle } = useLang();
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const year = new Date().getFullYear();
    const isAdmin = location.pathname.startsWith("/admin");

    const navItems = [
        { to: "/", label: t.nav.home, testid: "nav-home" },
        { to: "/department", label: t.nav.department, testid: "nav-department" },
        { to: "/office-structure", label: t.nav.structure, testid: "nav-structure" },
        { to: "/role", label: t.nav.role, testid: "nav-role" },
        { to: "/schemes", label: t.nav.schemes, testid: "nav-schemes" },
        { to: "/contact", label: t.nav.contact, testid: "nav-contact" },
    ];

    return (
        <div className="min-h-screen flex flex-col bg-white text-[#1A202C]">
            {/* Top Utility Bar */}
            <div className="bg-[#1A365D] text-white text-xs">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-2 flex items-center justify-between">
                    <span className="tracking-widest uppercase font-semibold">
                        {lang === "en" ? "Government of Tamil Nadu" : "தமிழ்நாடு அரசு"}
                    </span>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={toggle}
                            data-testid="lang-toggle"
                            className="tracking-widest uppercase font-semibold hover:text-[#DD6B20] transition-colors"
                        >
                            {lang === "en" ? "தமிழ்" : "English"}
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Nav */}
            <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/90 border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="flex items-center justify-between h-20">
                        <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
                            <div className="w-11 h-11 rounded-full bg-[#1A365D] text-white flex items-center justify-center border-2 border-[#DD6B20]">
                                <Landmark size={20} />
                            </div>
                            <div className="leading-tight">
                                <div className="font-serif font-bold text-lg md:text-xl text-[#1A365D]">
                                    {t.siteName}
                                </div>
                                <div className="small-caps text-gray-500 text-[10px]">
                                    {t.siteSubtitle}
                                </div>
                            </div>
                        </Link>

                        {/* Desktop Nav */}
                        <nav className="hidden lg:flex items-center gap-1">
                            {navItems.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    end={item.to === "/"}
                                    data-testid={item.testid}
                                    className={({ isActive }) =>
                                        `px-3 py-2 text-sm font-medium transition-colors relative ${
                                            isActive
                                                ? "text-[#1A365D]"
                                                : "text-gray-600 hover:text-[#1A365D]"
                                        }`
                                    }
                                >
                                    {({ isActive }) => (
                                        <>
                                            {item.label}
                                            {isActive && (
                                                <span className="absolute -bottom-1 left-3 right-3 h-[2px] bg-[#DD6B20]" />
                                            )}
                                        </>
                                    )}
                                </NavLink>
                            ))}
                        </nav>

                        <div className="flex items-center gap-2">
                            {user && user.role === "admin" ? (
                                <div className="hidden md:flex items-center gap-2">
                                    <Link
                                        to="/admin"
                                        data-testid="nav-admin-dashboard"
                                        className="text-xs uppercase tracking-widest font-semibold text-[#1A365D] hover:text-[#DD6B20] px-3"
                                    >
                                        <ShieldCheck size={16} className="inline mr-1" />
                                        Dashboard
                                    </Link>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={logout}
                                        data-testid="nav-logout-btn"
                                    >
                                        <LogOut size={14} className="mr-1" /> {t.nav.logout}
                                    </Button>
                                </div>
                            ) : (
                                <Link
                                    to="/admin/login"
                                    className="hidden md:inline-flex text-xs uppercase tracking-widest font-semibold text-[#1A365D] hover:text-[#DD6B20] px-3 py-2"
                                    data-testid="nav-admin-login"
                                >
                                    {t.nav.adminLogin}
                                </Link>
                            )}
                            <button
                                onClick={() => setOpen((v) => !v)}
                                className="lg:hidden p-2 text-[#1A365D]"
                                data-testid="mobile-menu-toggle"
                                aria-label="Menu"
                            >
                                {open ? <X size={22} /> : <Menu size={22} />}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile menu */}
                {open && (
                    <div className="lg:hidden border-t border-gray-200 bg-white">
                        <nav className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
                            {navItems.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    end={item.to === "/"}
                                    onClick={() => setOpen(false)}
                                    data-testid={`mobile-${item.testid}`}
                                    className={({ isActive }) =>
                                        `px-3 py-3 text-sm font-medium border-l-2 ${
                                            isActive
                                                ? "border-[#DD6B20] text-[#1A365D] bg-gray-50"
                                                : "border-transparent text-gray-600"
                                        }`
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                            {user && user.role === "admin" ? (
                                <>
                                    <Link
                                        to="/admin"
                                        onClick={() => setOpen(false)}
                                        className="px-3 py-3 text-sm font-medium text-[#1A365D]"
                                    >
                                        Admin Dashboard
                                    </Link>
                                    <button
                                        onClick={() => {
                                            setOpen(false);
                                            logout();
                                        }}
                                        className="px-3 py-3 text-sm font-medium text-left text-[#C53030]"
                                    >
                                        {t.nav.logout}
                                    </button>
                                </>
                            ) : (
                                <Link
                                    to="/admin/login"
                                    onClick={() => setOpen(false)}
                                    className="px-3 py-3 text-sm font-medium text-[#1A365D]"
                                >
                                    {t.nav.adminLogin}
                                </Link>
                            )}
                        </nav>
                    </div>
                )}
            </header>

            <main className="flex-1">{children}</main>

            {!isAdmin && (
                <footer className="bg-[#0F1E37] text-gray-300 mt-16">
                    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 md:grid-cols-12 gap-8">
                        <div className="md:col-span-5">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-11 h-11 rounded-full bg-white text-[#1A365D] flex items-center justify-center border-2 border-[#DD6B20]">
                                    <Landmark size={20} />
                                </div>
                                <div>
                                    <div className="font-serif font-bold text-lg text-white">
                                        {t.siteName}
                                    </div>
                                    <div className="small-caps text-gray-400 text-[10px]">
                                        {t.siteSubtitle}
                                    </div>
                                </div>
                            </div>
                            <p className="text-sm leading-relaxed text-gray-400">
                                {t.footer.about}
                            </p>
                        </div>

                        <div className="md:col-span-3">
                            <h4 className="small-caps text-white mb-4">
                                {t.footer.quickLinks}
                            </h4>
                            <ul className="space-y-2 text-sm">
                                {navItems.map((n) => (
                                    <li key={n.to}>
                                        <Link to={n.to} className="hover:text-[#DD6B20] transition-colors">
                                            {n.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="md:col-span-4">
                            <h4 className="small-caps text-white mb-4">
                                {t.footer.contact}
                            </h4>
                            <p className="text-sm text-gray-400 leading-relaxed">
                                {t.contact.hqAddress}
                                <br />
                                <span className="text-gray-300">{t.contact.hqPhone}</span>
                                <br />
                                <span className="text-gray-300">{t.contact.hqEmail}</span>
                            </p>
                        </div>
                    </div>
                    <div className="border-t border-white/10">
                        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 text-xs text-gray-500 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                            <span>{t.footer.copyright.replace("{year}", year)}</span>
                            <span className="italic">{t.footer.disclaimer}</span>
                        </div>
                    </div>
                </footer>
            )}
        </div>
    );
}
